import { Stat, StatGroup, StatLabel, StatNumber } from "@chakra-ui/react";
import { useCommitmentStatsRetrieveSuspense } from "gen/endpoints/commitment-stats/commitment-stats";
import React from "react";
import { useTranslation } from "react-i18next";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { formatMoney, subtractMoneyStrings } from "shared/utils/MoneyUtils";

export const CommitmentActivityTableStats: React.FC<{
  fundIds: string[] | undefined | null;
  fundFamilyId: string | undefined;
  investorId: string | undefined;
  investorEntityId: string | undefined;
  commitmentId: string | undefined;
}> = ({
  fundIds,
  fundFamilyId,
  investorId,
  investorEntityId,
  commitmentId,
}) => {
  const { t } = useTranslation(["commitment"]);

  const { data: stats } = useCommitmentStatsRetrieveSuspense({
    fund_ids: fundIds || undefined,
    fund_family_ids: fundFamilyId ? [fundFamilyId] : undefined,
    investor_id: investorId,
    investor_entity_id: investorEntityId,
    commitment_id: commitmentId,
  });

  const hasAnyValue = Object.values(stats).some((s) => Boolean(Number(s)));
  if (!hasAnyValue) {
    return null;
  }

  return (
    <TableBoxStats>
      <StatGroup>
        <Stat size="xs">
          <StatLabel>
            {t("commitment:edit.activity.headers.commitmentAmount.title")}
          </StatLabel>
          <StatNumber>{formatMoney(stats.total_commitment_amount)}</StatNumber>
        </Stat>
        <Stat size="xs">
          <StatLabel>
            {t("commitment:edit.activity.headers.calledAmount.title")}
          </StatLabel>
          <StatNumber>{formatMoney(stats.total_called_amount)}</StatNumber>
        </Stat>
        {
          // not every fund family has an uncalled calculation defined
          stats.total_uncalled_amount && (
            <Stat size="xs">
              <StatLabel>
                {t("commitment:edit.activity.headers.uncalledAmount.title")}
              </StatLabel>
              <StatNumber>
                {formatMoney(stats.total_uncalled_amount)}
              </StatNumber>
            </Stat>
          )
        }
        <Stat size="xs">
          <StatLabel>
            {t("commitment:edit.activity.headers.unfundedAmount.title")}
          </StatLabel>
          <StatNumber>{formatMoney(stats.total_unfunded_amount)}</StatNumber>
        </Stat>
        <Stat size="xs">
          <StatLabel>
            {t("commitment:edit.activity.headers.netDistributedAmount.title")}
          </StatLabel>
          <StatNumber>{formatMoney(stats.total_distributed_amount)}</StatNumber>
        </Stat>
        <Stat size="xs">
          <StatLabel>
            {t("commitment:edit.activity.headers.netCashMovement.title")}
          </StatLabel>
          <StatNumber>
            {formatMoney(
              subtractMoneyStrings(
                stats.total_called_amount,
                stats.total_distributed_amount,
              ),
            )}
          </StatNumber>
        </Stat>
        <Stat size="xs">
          <StatLabel>
            {t("commitment:edit.activity.headers.carryTaken.title")}
          </StatLabel>
          <StatNumber>{formatMoney(stats.total_carry_amount)}</StatNumber>
        </Stat>
      </StatGroup>
    </TableBoxStats>
  );
};
